<template>
  <div class="admin-dash flex flex-col flex-1 min-h-0 overflow-hidden">
    <!-- Header -->
    <header class="dash-header flex-shrink-0 flex justify-between items-center px-6 py-4">
      <div class="flex items-center gap-3">
        <h3 class="page-titles">
          Chatbot behaviour
        </h3>
      </div>
      <div class="header-actions">
        <button
          class="help-btn"
          title="How this dashboard works"
          aria-label="Help"
          @click="helpOpen = true"
        >
          ?
        </button>
        <router-link
          class="back-link"
          :to="{ name: 'Chatbot' }"
        >
          Back to chat
        </router-link>
      </div>
    </header>

    <div class="flex flex-1 min-h-0 overflow-hidden">
      <!-- Left rail: the context packs (agents) -->
      <aside class="agent-rail flex-shrink-0 overflow-y-auto">
        <div class="rail-heading">
          AI agents
        </div>
        <button
          v-for="c in contexts"
          :key="c.id"
          class="agent-item"
          :class="{ active: c.id === selectedId }"
          @click="selectPack(c.id)"
        >
          <span class="agent-icon">
            <cai-icon
              :name="iconFor(c)"
              :size="20"
            />
          </span>
          <span class="agent-text">
            <span class="agent-name">{{ c.name }}</span>
            <span class="agent-parent">{{ c.parent || '—' }}</span>
          </span>
        </button>

        <button
          class="agent-add"
          @click="openAddAgent"
        >
          <span class="agent-add-icon">+</span>
          <span>Add org/sport</span>
        </button>
      </aside>

      <!-- Main column -->
      <main class="dash-main flex-1 min-w-0 overflow-y-auto">
        <div
          v-if="loading"
          class="dash-empty"
        >
          Loading…
        </div>
        <div
          v-else-if="error"
          class="dash-error"
        >
          {{ error }}
        </div>

        <template v-else-if="model">
          <!-- Tabs (sticky) — Save stays visible while scrolling the long form -->
          <div class="dash-tabs">
            <button
              class="dash-tab"
              :class="{ on: tab === 'behaviour' }"
              @click="tab = 'behaviour'"
            >
              Behaviour
            </button>
            <button
              class="dash-tab"
              :class="{ on: tab === 'knowledge' }"
              @click="tab = 'knowledge'"
            >
              Knowledge files
            </button>
            <div
              v-if="tab === 'behaviour'"
              class="dash-tabs-actions"
            >
              <span
                v-if="saveFlash"
                class="save-note"
              >Saved — live on the next message.</span>
              <span
                v-else-if="dirty"
                class="dirty-note"
              >Unsaved changes</span>
              <button
                class="save-btn"
                :disabled="saving || !dirty"
                @click="save"
              >
                {{ saving ? 'Saving…' : (saveFlash ? 'Saved ✓' : 'Save changes') }}
              </button>
            </div>
            <div
              v-else-if="tab === 'knowledge'"
              class="dash-tabs-actions"
            >
              <button
                class="reingest-btn"
                :disabled="reingest.running"
                @click="reingestConfirm = true"
              >
                {{ reingest.running ? 'Re-ingesting…' : 'Full re-ingest' }}
              </button>
            </div>
          </div>

          <!-- ── Behaviour editor ── -->
          <section
            v-if="tab === 'behaviour'"
            class="editor"
          >
            <!-- Identity -->
            <div class="field">
              <span class="field-label">Name</span>
              <span class="field-hint">What shows up in the agent list and in the chat picker — the club or context's plain name, e.g. "Handball" or "Hammarby".</span>
              <input
                v-model="model.name"
                class="field-input full"
              >
            </div>
            <label class="field">
              <span class="field-label">Description</span>
              <span class="field-hint">A one- or two-line summary shown wherever this agent is listed, so someone picking an agent knows what it's for at a glance.</span>
              <textarea
                v-model="model.description"
                v-autosize
                class="field-textarea"
                placeholder="One or two lines describing this agent."
              />
            </label>

            <!-- Persona -->
            <label class="field">
              <span class="field-label">Persona</span>
              <span class="field-hint">
                Who the agent is and how it should think — the main instruction the model reads before every answer.
                Write it like a short job description: who it's talking to, what it should focus on, what kind of
                answer it should give. E.g. "You are Handbollshjärnan, a handball performance-cognition analyst.
                You help coaches turn a player's cognitive profile into concrete court decisions…".
              </span>
              <textarea
                v-model="model.persona"
                v-autosize
                class="field-textarea"
                placeholder="Describe who this agent is and how it should behave…"
              />
            </label>

            <!-- Context parameters -->
            <div class="field">
              <span class="field-label">
                Context parameters
                <span
                  v-if="isInherited('context_params')"
                  class="inherited-badge"
                >inherited from base</span>
              </span>
              <span class="field-hint">
                Free-form dials describing how demanding this environment is — helps the agent judge what a
                player/candidate is dealing with. Add whatever labels make sense here; there's no fixed list.
                E.g. key "speed" → value "very_high", key "contact" → value "high".
              </span>
              <kv-editor
                v-model="model.context_params"
                value-placeholder="e.g. high"
                add-label="Add parameter"
              />
            </div>

            <!-- Behaviour / response style -->
            <div class="field">
              <span class="field-label">
                Response style &amp; behaviour
                <span
                  v-if="isInherited('behavior')"
                  class="inherited-badge"
                >inherited from base</span>
              </span>
              <span class="field-hint">
                One entry per aspect of HOW the agent answers (not what it knows). Common keys: "response_length"
                (how long/short a reply should be), "tone" (formal, casual, coach-like…), "formatting" (headings,
                bullet points, emoji or not), "framing" (how it should reason about a topic). Add a key, then write
                a sentence or two describing that aspect for this agent.
              </span>
              <kv-editor
                v-model="model.behavior"
                value-placeholder="describe this aspect…"
                add-label="Add behaviour field"
              />
            </div>

            <!-- Roles -->
            <div class="field">
              <span class="field-label">
                Roles
                <span
                  v-if="isInherited('roles')"
                  class="inherited-badge"
                >inherited from base</span>
              </span>
              <span class="field-hint">
                The different people who might ask this agent questions — a coach, a scout, a player — each getting
                answers tailored to what THEY care about. Add one card per audience below.
              </span>
              <div
                v-for="(r, i) in model.roles"
                :key="'role' + i"
                class="card"
              >
                <div class="card-head">
                  <div class="card-grid">
                    <label class="subfield">
                      <span class="subfield-label">Role key <span class="subfield-opt">(links this role to the chat's audience picker)</span></span>
                      <input
                        v-model="r.id"
                        class="field-input"
                        placeholder="head_coach"
                      >
                    </label>
                    <label class="subfield">
                      <span class="subfield-label">Name</span>
                      <input
                        v-model="r.name"
                        class="field-input"
                        placeholder="Head Coach"
                      >
                    </label>
                  </div>
                  <button
                    class="card-remove"
                    title="Remove role"
                    @click="model.roles.splice(i, 1)"
                  >
                    ✕
                  </button>
                </div>
                <label class="subfield">
                  <span class="subfield-label">Focus</span>
                  <textarea
                    v-model="r.focus"
                    v-autosize
                    class="field-textarea"
                    placeholder="What this role cares about"
                  />
                </label>
                <label class="subfield">
                  <span class="subfield-label">Audience filter <span class="subfield-opt">(optional)</span></span>
                  <input
                    v-model="r.audience"
                    class="field-input"
                    placeholder="coach | test_leader | sales"
                  >
                </label>
                <label class="subfield">
                  <span class="subfield-label">Emphasis measures <span class="subfield-opt">(optional)</span></span>
                  <str-list-editor
                    v-model="r.emphasis"
                    :multiline="false"
                    placeholder="e.g. working_memory"
                    add-label="Add measure"
                  />
                </label>
                <label class="subfield">
                  <span class="subfield-label">Typical questions <span class="subfield-opt">(optional)</span></span>
                  <str-list-editor
                    v-model="r.key_questions"
                    placeholder="A question this role typically asks"
                    add-label="Add question"
                  />
                </label>
              </div>
              <button
                class="row-add"
                @click="addRole"
              >
                + Add role
              </button>
            </div>

            <!-- Translation targets -->
            <div class="field">
              <span class="field-label">
                Translation targets
                <span
                  v-if="isInherited('translation_targets')"
                  class="inherited-badge"
                >inherited from base</span>
              </span>
              <span class="field-hint">
                What a person's cognitive profile gets turned INTO for this context — playing positions in a
                sport, job roles in a business. Each target lists which measures matter most for it ("emphasises")
                and a short blurb explaining why, so the agent can say e.g. "this profile fits a playmaker because…".
              </span>
              <div
                v-for="(t, i) in model.translation_targets"
                :key="'tt' + i"
                class="card"
              >
                <div class="card-head">
                  <div class="card-grid">
                    <label class="subfield">
                      <span class="subfield-label">Id</span>
                      <input
                        v-model="t.id"
                        class="field-input"
                        placeholder="striker"
                      >
                    </label>
                    <label class="subfield">
                      <span class="subfield-label">Name</span>
                      <input
                        v-model="t.name"
                        class="field-input"
                        placeholder="Striker"
                      >
                    </label>
                  </div>
                  <button
                    class="card-remove"
                    title="Remove target"
                    @click="model.translation_targets.splice(i, 1)"
                  >
                    ✕
                  </button>
                </div>
                <label class="subfield">
                  <span class="subfield-label">Emphasises</span>
                  <str-list-editor
                    v-model="t.emphasises"
                    :multiline="false"
                    placeholder="e.g. impulse_control"
                    add-label="Add measure"
                  />
                </label>
                <label class="subfield">
                  <span class="subfield-label">Blurb</span>
                  <textarea
                    v-model="t.blurb"
                    v-autosize
                    class="field-textarea"
                    placeholder="One line about this target"
                  />
                </label>
              </div>
              <button
                class="row-add"
                @click="model.translation_targets.push({ id: '', name: '', emphasises: [], blurb: '' })"
              >
                + Add translation target
              </button>
            </div>

            <!-- Measure families -->
            <div class="field">
              <span class="field-label">
                Measure families
                <span
                  v-if="isInherited('measure_families')"
                  class="inherited-badge"
                >inherited from base</span>
              </span>
              <span class="field-hint">
                How to interpret each personality / self-report test family (e.g. Big Five, impulsivity screening)
                for THIS context specifically — these are self-report scores, not performance scores, so the note
                should explain what a high or low result actually means for someone here. The Id must match the
                assessment type in the underlying data exactly (e.g. "BFI10").
              </span>
              <div
                v-for="(m, i) in model.measure_families"
                :key="'mf' + i"
                class="card"
              >
                <div class="card-head">
                  <div class="card-grid">
                    <label class="subfield">
                      <span class="subfield-label">Id</span>
                      <input
                        v-model="m.id"
                        class="field-input"
                        placeholder="BFI10"
                      >
                    </label>
                    <label class="subfield">
                      <span class="subfield-label">Name</span>
                      <input
                        v-model="m.name"
                        class="field-input"
                        placeholder="Big Five personality (BFI-10)"
                      >
                    </label>
                  </div>
                  <button
                    class="card-remove"
                    title="Remove family"
                    @click="model.measure_families.splice(i, 1)"
                  >
                    ✕
                  </button>
                </div>
                <label class="subfield">
                  <span class="subfield-label">Note</span>
                  <textarea
                    v-model="m.note"
                    v-autosize
                    class="field-textarea"
                    placeholder="How to interpret this family in this context"
                  />
                </label>
              </div>
              <button
                class="row-add"
                @click="model.measure_families.push({ id: '', name: '', note: '' })"
              >
                + Add measure family
              </button>
            </div>

            <!-- Guardrails -->
            <div class="field">
              <span class="field-label">
                Guardrails (hard rules)
                <span
                  v-if="isInherited('hard_rules')"
                  class="inherited-badge"
                >inherited from base</span>
              </span>
              <span class="field-hint">
                Rules the agent must NEVER break, no matter what it's asked — e.g. "never state a score you didn't
                get from a tool" or "never call cognitive abilities trainable through practice". Each line is one
                rule. This list REPLACES the inherited rules if you add anything here, so re-add any inherited
                rule you still want to keep, not just the new ones.
              </span>
              <str-list-editor
                v-model="model.hard_rules"
                placeholder="A rule the agent must always follow"
                add-label="Add guardrail"
              />
            </div>

            <!-- RAG guardrails -->
            <div class="field">
              <span class="field-label">
                Knowledge-search guardrails
                <span
                  v-if="isInherited('rag_hard_rules')"
                  class="inherited-badge"
                >inherited from base</span>
              </span>
              <span class="field-hint">
                Extra rules that only kick in when the agent is answering from the knowledge documents (not test
                data) — e.g. "always cite the source document" or "don't answer from a document marked needs_review".
                Same rule as above: any addition here replaces the inherited list wholesale.
              </span>
              <str-list-editor
                v-model="model.rag_hard_rules"
                placeholder="A rule for knowledge-grounded answers"
                add-label="Add rule"
              />
            </div>

            <!-- Routing rules -->
            <div class="field">
              <span class="field-label">
                Routing rules
                <span
                  v-if="isInherited('routing_rules')"
                  class="inherited-badge"
                >inherited from base</span>
              </span>
              <span class="field-hint">
                Tells the agent when to use a data tool (player scores) versus the knowledge search versus just
                answering directly — e.g. "if the user names a specific player, look up their scores first". Same
                rule as above: any addition here replaces the inherited list wholesale.
              </span>
              <str-list-editor
                v-model="model.routing_rules"
                placeholder="A routing rule"
                add-label="Add rule"
              />
            </div>

            <!-- Knowledge routing text -->
            <label class="field">
              <span class="field-label">
                Knowledge-search routing
                <span
                  v-if="isInherited('rag_routing')"
                  class="inherited-badge"
                >inherited from base</span>
              </span>
              <span class="field-hint">
                A short paragraph guiding WHEN the agent should search the knowledge base at all — e.g. "search
                whenever the user asks about tactics, methodology or anything not covered by test-score data".
                This only applies once this agent's documents have been ingested and indexed.
              </span>
              <textarea
                v-model="model.rag_routing"
                v-autosize
                class="field-textarea"
                placeholder="Guidance for when/how to search the knowledge base"
              />
            </label>

            <!-- Corpus hints -->
            <div class="field">
              <span class="field-label">Corpus hints</span>
              <span class="field-hint">
                Documentation only — a checklist for whoever is gathering documents for this agent, listing the
                kinds of material its knowledge base should have. E.g. "Position guides", "Assessment/analysis
                method", "Domain-specific research the club trusts". Doesn't affect the agent's behaviour.
              </span>
              <str-list-editor
                v-model="model.corpus_hint"
                placeholder="A kind of document to provide"
                add-label="Add hint"
              />
            </div>

            <!-- Default language -->
            <div class="field narrow">
              <span class="field-label">Default language</span>
              <span class="field-hint">The language the agent replies in when the user doesn't imply one. Use a short code, e.g. "en" or "sv".</span>
              <input
                v-model="model.language_default"
                class="field-input"
                placeholder="en"
              >
            </div>
          </section>

          <!-- ── Knowledge files ── -->
          <section
            v-else
            class="knowledge"
          >
            <div
              v-if="(reingest.running || reingest.log.length) && !reingestDismissed"
              class="reingest-status"
              :class="{ done: !reingest.running && reingest.ok === true, failed: reingest.ok === false }"
            >
              <div class="reingest-head">
                <span v-if="reingest.running">⏳ Rebuilding the search index — this can take 10–30 minutes.</span>
                <span v-else-if="reingest.ok === true">✓ Re-ingest complete.</span>
                <span v-else-if="reingest.ok === false">✗ Re-ingest finished with errors.</span>
                <button
                  v-if="!reingest.running"
                  class="notice-close"
                  title="Dismiss"
                  @click="dismissReingest"
                >
                  ✕
                </button>
              </div>

              <template v-if="reingest.orgs.length">
                <div class="reingest-bar-track">
                  <div
                    class="reingest-bar-fill"
                    :class="{ failed: reingest.ok === false }"
                    :style="{ width: reingestPercent + '%' }"
                  />
                </div>
                <div class="reingest-bar-label">
                  {{ reingestDoneCount }} / {{ reingest.orgs.length }} orgs
                </div>

                <ul class="reingest-orgs">
                  <li
                    v-for="org in reingest.orgs"
                    :key="org.id"
                    class="reingest-org"
                    :class="'is-' + org.status"
                    :title="org.detail || ''"
                  >
                    <span class="reingest-org-icon">
                      <template v-if="org.status === 'ok'">✓</template>
                      <template v-else-if="org.status === 'failed'">✗</template>
                      <template v-else-if="org.status === 'running'">⏳</template>
                      <template v-else>○</template>
                    </span>
                    {{ org.id }}
                  </li>
                </ul>
              </template>

              <pre
                v-if="reingest.log.length"
                class="reingest-log"
              >{{ reingest.log.join('\n') }}</pre>
            </div>

            <div
              v-if="uploading || uploadMsg"
              class="upload-status"
              :class="{ ok: uploadState === 'ok', warn: uploadState === 'warn', failed: uploadState === 'failed', indexing: uploadState === 'indexing' }"
            >
              <span v-if="uploading">⏳ Uploading “{{ uploadName }}”…</span>
              <template v-else>
                <span v-if="uploadState === 'indexing'">⏳ {{ uploadMsg }}</span>
                <span v-else>{{ uploadMsg }}</span>
                <button
                  class="notice-close"
                  title="Dismiss"
                  @click="uploadMsg = ''"
                >
                  ✕
                </button>
              </template>
            </div>

            <!-- Stale index reminder (shown after rename/move until re-ingest) -->
            <div
              v-if="indexStale && !reingest.running"
              class="stale-banner"
            >
              <span>⚠ The search index may not reflect recent folder changes. Use the Full re-ingest button above.</span>
              <button
                class="notice-close"
                title="Dismiss"
                @click="indexStale = false"
              >
                ✕
              </button>
            </div>

            <label class="upload-pii-toggle">
              <input
                v-model="uploadAllowPii"
                type="checkbox"
              >
              Skip personal-data guardrail (only for content with no real candidate data, e.g. research papers)
            </label>

            <div class="knowledge-body">
              <div class="knowledge-tree-col">
                <div
                  v-if="!visibleTree.length && !indexStale"
                  class="dash-empty"
                >
                  No knowledge files for this agent yet.
                </div>
                <file-tree
                  v-if="visibleTree.length"
                  :nodes="visibleTree"
                  :open-paths="openPaths"
                  :skip-info="skipInfo"
                  @toggle="toggleFolder"
                  @select-folder="onUploadHere"
                  @rename-item="onRenameItem"
                  @move-item="onMoveItem"
                  @create-folder="onCreateFolder"
                  @delete-item="onDeleteItem"
                  @toggle-skip="onToggleSkip"
                />
              </div>

              <!-- Org-wide: every agent's not-yet-live files, regardless of which
                   agent is selected on the left — see notLiveFiles. -->
              <aside class="knowledge-status-col">
                <h4 class="knowledge-status-title">
                  Not yet live
                  <span class="knowledge-status-count">{{ notLiveFiles.length }}</span>
                </h4>
                <p class="knowledge-status-hint">
                  Every knowledge file across the whole org that's saved to disk but not currently searchable in
                  chat (not indexed in the vector database yet, or indexing failed).
                </p>
                <div
                  v-if="!notLiveFiles.length"
                  class="knowledge-status-empty"
                >
                  ✓ Everything on disk is indexed and live.
                </div>
                <!-- Grouped by top-level folder (general/football/handball/…) so
                     that repeated prefix can be dropped from each path below. -->
                <div
                  v-else
                  class="knowledge-status-groups"
                >
                  <div
                    v-for="group in notLiveByAgent"
                    :key="group.agent"
                    class="knowledge-status-group"
                  >
                    <h5 class="knowledge-status-group-title">
                      {{ group.agent }}
                      <span class="knowledge-status-group-count">{{ group.files.length }}</span>
                    </h5>
                    <ul class="knowledge-status-list">
                      <li
                        v-for="f in group.files"
                        :key="f.path"
                        class="knowledge-status-item"
                      >
                        <span
                          class="knowledge-status-path"
                          :title="f.path"
                        >{{ f.rest }}</span>
                        <span class="live-badge live-no">not live</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </aside>
            </div>

            <!-- Hidden picker, opened by a folder's "Upload here" button -->
            <input
              ref="uploader"
              type="file"
              accept=".pdf,.docx,.pptx,.md"
              style="display: none"
              @change="onFilePicked"
            >
          </section>
        </template>
      </main>
    </div>

    <!-- Full re-ingest confirmation -->
    <div
      v-if="reingestConfirm"
      class="dash-scrim"
      @click.self="reingestConfirm = false"
    >
      <div class="dash-modal">
        <h4 class="dash-modal-title">
          Rebuild the entire search index?
        </h4>
        <p class="dash-modal-body">
          This wipes and rebuilds every agent's knowledge index from the documents currently
          on disk. Use it after moving or reorganising files (like tidying the folders).
        </p>
        <ul class="dash-modal-points">
          <li>Takes <strong>10–30 minutes</strong>; answers may be degraded until it finishes.</li>
          <li>The current index is <strong>replaced</strong> — this can't be undone.</li>
          <li>Everyday uploads don't need this — new files are indexed automatically.</li>
        </ul>
        <div class="dash-modal-actions">
          <button
            class="dash-modal-cancel"
            @click="reingestConfirm = false"
          >
            Cancel
          </button>
          <button
            class="dash-modal-confirm"
            @click="doReingest"
          >
            Yes, rebuild the index
          </button>
        </div>
      </div>
    </div>

    <!-- Move-to folder picker -->
    <div
      v-if="moveTarget"
      class="dash-scrim"
      @click.self="cancelMove"
    >
      <div class="dash-modal">
        <h4 class="dash-modal-title">
          Move "{{ moveTarget.name }}"
        </h4>
        <p class="dash-modal-body">
          Choose the destination folder:
        </p>
        <select
          v-model="moveDest"
          class="move-select"
        >
          <option value="">
            — pick a folder —
          </option>
          <option
            v-for="p in allFolderPaths"
            :key="p"
            :value="p"
          >
            {{ p }}
          </option>
        </select>
        <p
          v-if="moveError"
          class="move-error"
        >
          {{ moveError }}
        </p>
        <div class="dash-modal-actions">
          <button
            class="dash-modal-cancel"
            @click="cancelMove"
          >
            Cancel
          </button>
          <button
            class="dash-modal-confirm"
            :disabled="!moveDest"
            @click="confirmMove"
          >
            Move here
          </button>
        </div>
      </div>
    </div>

    <!-- Create folder -->
    <div
      v-if="createFolderParent !== null"
      class="dash-scrim"
      @click.self="cancelCreateFolder"
    >
      <div class="dash-modal">
        <h4 class="dash-modal-title">
          New folder
        </h4>
        <p class="dash-modal-body">
          Inside <code>{{ createFolderParent || '(root)' }}</code>
        </p>
        <input
          ref="createFolderInput"
          v-model="createFolderName"
          class="field-input full"
          placeholder="e.g. research"
          @keydown.enter="submitCreateFolder"
        >
        <p
          v-if="createFolderError"
          class="move-error"
        >
          {{ createFolderError }}
        </p>
        <div class="dash-modal-actions">
          <button
            class="dash-modal-cancel"
            @click="cancelCreateFolder"
          >
            Cancel
          </button>
          <button
            class="dash-modal-confirm"
            :disabled="!createFolderName.trim() || createFolderBusy"
            @click="submitCreateFolder"
          >
            {{ createFolderBusy ? 'Creating…' : 'Create folder' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Unsaved changes guard -->
    <div
      v-if="leaveModal"
      class="dash-scrim"
      @click.self="cancelLeave"
    >
      <div class="dash-modal">
        <h4 class="dash-modal-title">
          Leave without saving?
        </h4>
        <p class="dash-modal-body">
          You have unsaved changes to <strong>{{ (model && model.name) || 'this agent' }}</strong>.
          If you leave now, they'll be lost.
        </p>
        <div class="dash-modal-actions">
          <button
            class="dash-modal-cancel"
            @click="cancelLeave"
          >
            Keep editing
          </button>
          <button
            class="dash-modal-confirm"
            @click="confirmLeave"
          >
            Discard changes
          </button>
        </div>
      </div>
    </div>

    <!-- Add org/sport -->
    <div
      v-if="addAgentOpen"
      class="dash-scrim"
      @click.self="closeAddAgent"
    >
      <div class="dash-modal">
        <h4 class="dash-modal-title">
          Add a new org / sport
        </h4>
        <p class="dash-modal-body">
          This creates a blank agent — same structure as the others, with every
          field empty — plus an empty knowledge folder for its documents. It shows
          up in the list on the left right away; nothing is live for chat until you
          fill it in, save it, upload its knowledge files and run a re-ingest.
        </p>
        <label class="field">
          <span class="field-label">Name</span>
          <input
            ref="addAgentNameInput"
            v-model="addAgentName"
            class="field-input full"
            placeholder="e.g. Basketball"
            @keydown.enter="submitAddAgent"
          >
        </label>
        <p
          v-if="addAgentSlug"
          class="add-agent-slug"
        >
          Will be created as <code>{{ addAgentSlug }}</code>
        </p>
        <label class="field">
          <span class="field-label">Type</span>
          <select
            v-model="addAgentParent"
            class="field-input full"
          >
            <option value="sport">
              Sport
            </option>
            <option value="business">
              Business
            </option>
            <option value="education">
              Education
            </option>
          </select>
        </label>
        <p
          v-if="addAgentError"
          class="move-error"
        >
          {{ addAgentError }}
        </p>
        <div class="dash-modal-actions">
          <button
            class="dash-modal-cancel"
            @click="closeAddAgent"
          >
            Cancel
          </button>
          <button
            class="dash-modal-confirm"
            :disabled="!addAgentName.trim() || addAgentBusy"
            @click="submitAddAgent"
          >
            {{ addAgentBusy ? 'Creating…' : 'Create' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Help -->
    <div
      v-if="helpOpen"
      class="dash-scrim"
      @click.self="helpOpen = false"
    >
      <div class="dash-modal help-modal">
        <h4 class="dash-modal-title">
          Using this dashboard
        </h4>
        <p class="dash-modal-body">
          The list on the left is the set of <strong>agents</strong> — one per
          org/sport, plus the shared "General" agent. Pick one to edit it. A
          new agent shows up in the chat start picker and can be queried as
          soon as it's created — uploading its knowledge files indexes them
          in the background, no extra step needed.
        </p>
        <h5 class="help-subhead">
          Behaviour tab
        </h5>
        <p class="dash-modal-body">
          Defines how this agent talks and thinks: its persona, the audiences
          (roles) it tailors answers for, its guardrails and rules. Changes here
          take effect on the agent's very next chat reply once you hit
          <strong>Save changes</strong> — no restart or re-ingest needed.
        </p>
        <h5 class="help-subhead">
          Knowledge files tab
        </h5>
        <p class="dash-modal-body">
          The documents this agent can search when answering (PDFs, Word docs,
          slides, markdown). Uploading a file indexes it immediately. Renaming or
          moving files does <strong>not</strong> re-index them — use
          <strong>Full re-ingest</strong> afterwards so search reflects the new
          layout. Baseline documents (shipped with the app) can't be renamed;
          only files you've uploaded can.
        </p>
        <h5 class="help-subhead">
          Adding a new org/sport
        </h5>
        <p class="dash-modal-body">
          Use <strong>+ Add org/sport</strong> at the bottom of the list to
          scaffold a brand-new blank agent, ready for you to fill in and upload
          documents to.
        </p>
        <div class="dash-modal-actions">
          <button
            class="dash-modal-confirm"
            @click="helpOpen = false"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FileTree from '../components/FileTree.vue'
import KvEditor from '../components/KvEditor.vue'
import StrListEditor from '../components/StrListEditor.vue'
import CaiIcon from '../components/CaiIcon.vue'
import autosize from '../autosize'
import { getContexts, getAdminContext, saveAdminContext, createAdminContext, getAdminFiles, uploadKnowledgeFile, getUploadStatus, startReingest, getReingestStatus, renameKnowledgeItem, moveKnowledgeItem, createKnowledgeFolder, deleteKnowledgeItem, getSkipDirs, setSkipDir } from '../api'

// Fields the editor surfaces. On save, each is written to the pack's own YAML
// only when it differs from _base (otherwise it stays inherited). Everything the
// editor does NOT surface (e.g. process_model) is preserved untouched.
const EDITED_FIELDS = [
  'status', 'name', 'description', 'persona', 'context_params', 'behavior', 'roles',
  'translation_targets', 'measure_families', 'hard_rules', 'rag_hard_rules',
  'routing_rules', 'rag_routing', 'corpus_hint', 'language_default'
]
// Identity fields: always kept in the pack file, never treated as inherited.
const ALWAYS_KEEP = new Set(['status', 'name', 'description'])

function clone (v) {
  return v === undefined ? undefined : JSON.parse(JSON.stringify(v))
}

function deepEqual (a, b) {
  return JSON.stringify(a) === JSON.stringify(b)
}

// Merge override ON TOP of base: dicts merge key-by-key, arrays/scalars replace —
// mirrors engine/packs._deep_merge so the editor shows the same effective pack.
function deepMerge (base, over) {
  const out = Object.assign({}, base)
  Object.keys(over || {}).forEach(k => {
    const bv = out[k]; const ov = over[k]
    if (bv && ov && typeof bv === 'object' && !Array.isArray(bv) && typeof ov === 'object' && !Array.isArray(ov)) {
      out[k] = deepMerge(bv, ov)
    } else {
      out[k] = ov
    }
  })
  return out
}

function isEmpty (v) {
  if (v == null) return true
  if (typeof v === 'string') return v.trim() === ''
  if (Array.isArray(v)) return v.length === 0
  if (typeof v === 'object') return Object.keys(v).length === 0
  return false
}

export default {
  name: 'AdminDashboard',

  components: { FileTree, KvEditor, StrListEditor, CaiIcon },

  directives: { autosize },

  data () {
    return {
      contexts: [],
      selectedId: '',
      raw0: {}, // the pack's own YAML as loaded (to preserve unsurfaced fields)
      base: {}, // _base.yaml (what's inherited)
      model: null, // effective, editable (base merged with raw0)
      loading: false,
      error: '',
      saving: false,
      saveFlash: false,
      tab: 'behaviour',
      snapshot: '', // JSON of the model as last loaded/saved — for dirty detection
      leaveModal: false,
      pendingAgent: null, // agent id to switch to once the user confirms discarding
      pendingNext: null, // vue-router next() callback held while confirming a route leave
      // Knowledge tree
      files: [],
      folders: [], // empty-folder paths (see /admin/files) — invisible via files alone
      openPaths: {},
      // Which top-level folder(s) under the selected agent's own corpus are
      // excluded from ingestion (like the pre-existing 'Rapporter' exclusion),
      // and that corpus's root path — see FileTree's skip-toggle.
      skipInfo: { corpusPrefix: '', skipDirs: [] },
      // Upload
      uploadTarget: '', // folder path chosen via a folder's "Upload here"
      uploading: false,
      uploadName: '',
      uploadMsg: '',
      uploadState: null, // 'ok' | 'warn' | 'failed' | 'indexing' | null
      uploadAllowPii: false,
      uploadTimer: null,
      // Full re-ingest
      reingestConfirm: false,
      reingest: { running: false, log: [], ok: null, orgs: [], finished_at: null },
      reingestTimer: null,
      // Persisted across reloads/logins, keyed by the completed run's own
      // finished_at timestamp — so dismissing survives a refresh but a NEW
      // re-ingest (a fresh finished_at once it completes) shows the banner
      // again regardless of what was dismissed before. See reingestDismissed.
      reingestDismissedAt: this.loadReingestDismissedAt(),
      // Stale-index reminder: shown after rename/move until re-ingest is run.
      indexStale: false,
      // Move-to modal
      moveTarget: null,
      moveDest: '',
      moveError: '',
      // Create-folder modal — parent is a path string once open, null when closed
      createFolderParent: null,
      createFolderName: '',
      createFolderError: '',
      createFolderBusy: false,
      // Add org/sport modal
      addAgentOpen: false,
      addAgentName: '',
      addAgentParent: 'sport',
      addAgentError: '',
      addAgentBusy: false,
      // Help modal
      helpOpen: false
    }
  },

  computed: {
    fileTree () {
      return this.buildTree(this.files, this.folders)
    },
    // Org-level re-ingest progress: each org is one step (pending -> running ->
    // ok/failed), not file/chunk-level — the bulk ingest subprocess only
    // reports its detailed output once it exits, so a step completes all at
    // once rather than crawling up gradually.
    reingestDoneCount () {
      return this.reingest.orgs.filter(o => o.status === 'ok' || o.status === 'failed').length
    },
    reingestPercent () {
      const total = this.reingest.orgs.length
      return total ? Math.round((this.reingestDoneCount / total) * 100) : 0
    },
    // Hides the completed-run banner once the user dismisses THIS run
    // specifically (matched by the backend's own finished_at timestamp, so
    // it stays hidden across reloads/logins but reappears for the next run).
    // Never hides an actively running re-ingest — only !running completions.
    reingestDismissed () {
      return !this.reingest.running && !!this.reingest.finished_at && this.reingest.finished_at === this.reingestDismissedAt
    },
    // A sport/org agent sees only its OWN folder — never the shared "general" folder
    // or another org's knowledge. The general agent's view shows every org/sport folder.
    // A "contexts" folder is hidden everywhere, at any depth, not just at the top —
    // it's the live context-pack YAMLs (engine/packs.py's _VOLUME_ROOT / "contexts"
    // on the persisted volume), never something an admin uploads into or browses.
    // This is VIEW-only and does not change what an agent actually searches.
    visibleTree () {
      const noContext = this.stripNamed(this.fileTree, 'contexts')
      if (this.selectedId === 'general') return noContext
      const keep = new Set([this.selectedId])
      return noContext.filter(n => keep.has(n.name))
    },
    // Org-wide, agent-independent: every knowledge file on disk (any sport/agent,
    // not just the one currently selected) whose `indexed` ground-truth flag
    // came back false from /admin/files — i.e. saved but not (yet, or no longer)
    // searchable in chat. Restricted to each org's actual RAG corpus — the
    // "<agent>/knowledge/…" folder (what an org's corpus_dir in org.yaml points
    // at for football/handball, and what general is being migrated to match) —
    // so the org's own structured-data exports (e.g. "football/org/ifk/data")
    // and per-person report PDFs ("Rapporter"), which are never meant to be
    // ingested, don't drown real gaps in expected non-matches.
    notLiveFiles () {
      return this.files
        .filter(f => !f.indexed && f.path.split('/')[1] === 'knowledge')
        .slice()
        .sort((a, b) => a.path.localeCompare(b.path))
    },
    // notLiveFiles grouped by its top-level folder (general/football/handball/…)
    // so the panel can drop that repeated prefix from each path and stay
    // readable instead of one long flat list of near-identical-looking paths.
    notLiveByAgent () {
      const groups = {}
      this.notLiveFiles.forEach(f => {
        const parts = f.path.split('/')
        const agent = parts[0]
        const rest = parts.length > 1 ? parts.slice(1).join('/') : f.path
        if (!groups[agent]) groups[agent] = []
        groups[agent].push({ path: f.path, rest })
      })
      return Object.keys(groups).sort().map(agent => ({ agent, files: groups[agent] }))
    },
    // Unsaved edits: the model differs from the snapshot taken on load/save.
    dirty () {
      return !!this.model && this.snapshot !== '' && JSON.stringify(this.model) !== this.snapshot
    },
    // Flat list of all folder paths in the current file tree (for move picker).
    allFolderPaths () {
      const paths = ['(root)']
      const walk = (nodes) => nodes.forEach(n => {
        if (n.type === 'folder') { paths.push(n.path); walk(n.children) }
      })
      walk(this.fileTree)
      return paths
    },
    // Mirrors the backend's slugify so the user sees the id before creating.
    addAgentSlug () {
      return this.addAgentName.trim().replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '').toLowerCase()
    }
  },

  created () {
    this.loadContexts()
    this.loadFiles()
    this.pollReingest()
    window.addEventListener('beforeunload', this.onBeforeUnload)
  },

  beforeDestroy () {
    if (this.reingestTimer) clearTimeout(this.reingestTimer)
    if (this.uploadTimer) clearTimeout(this.uploadTimer)
    window.removeEventListener('beforeunload', this.onBeforeUnload)
  },

  // Guard navigating away from the dashboard (e.g. "Back to chat") when dirty.
  beforeRouteLeave (to, from, next) {
    if (this.dirty) {
      this.pendingNext = next
      this.leaveModal = true
    } else {
      next()
    }
  },

  methods: {
    async loadContexts () {
      try {
        const { data } = await getContexts()
        // 'business' agents were retired (contexts/business.yaml removed) — hide any
        // stale entry the API still returns for one.
        this.contexts = (data.contexts || []).filter(c => c.parent !== 'business')
        if (this.contexts.length) this.selectPack(this.contexts[0].id)
      } catch (err) {
        this.error = 'Could not load agents. ' + this.errText(err)
      }
    },

    // Switching agent while there are unsaved edits asks first.
    selectPack (id) {
      if (id === this.selectedId) return
      if (this.dirty) {
        this.pendingAgent = id
        this.leaveModal = true
        return
      }
      this.loadPack(id)
    },

    onBeforeUnload (e) {
      if (this.dirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    },

    cancelLeave () {
      // Stay put: reject a pending route-leave, forget any pending agent switch.
      if (this.pendingNext) { this.pendingNext(false); this.pendingNext = null }
      this.pendingAgent = null
      this.leaveModal = false
    },

    confirmLeave () {
      this.leaveModal = false
      this.snapshot = JSON.stringify(this.model) // treat current state as clean so guards don't re-fire
      if (this.pendingNext) {
        const next = this.pendingNext; this.pendingNext = null
        next()
      } else if (this.pendingAgent) {
        const id = this.pendingAgent; this.pendingAgent = null
        this.loadPack(id)
      }
    },

    async loadPack (id) {
      this.selectedId = id
      this.loading = true
      this.error = ''
      this.saveFlash = false
      this.loadSkipDirs(id)
      try {
        const { data } = await getAdminContext(id)
        this.raw0 = data.raw || {}
        this.base = data.base || {}
        const eff = deepMerge(this.base, this.raw0)
        // Ensure the collections the editor binds to always exist.
        eff.roles = (eff.roles || []).map(r => Object.assign(
          { id: '', name: '', focus: '', audience: '', emphasis: [], key_questions: [] }, r
        ))
        eff.translation_targets = (eff.translation_targets || []).map(t => Object.assign(
          { id: '', name: '', emphasises: [], blurb: '' }, t
        ))
        eff.measure_families = (eff.measure_families || []).map(m => Object.assign(
          { id: '', name: '', note: '' }, m
        ))
        eff.status = eff.status || 'template'
        eff.context_params = eff.context_params || {}
        eff.behavior = eff.behavior || {}
        eff.hard_rules = eff.hard_rules || []
        eff.rag_hard_rules = eff.rag_hard_rules || []
        eff.routing_rules = eff.routing_rules || []
        eff.corpus_hint = eff.corpus_hint || []
        this.model = eff
        this.$nextTick(() => { this.snapshot = JSON.stringify(this.model) })
      } catch (err) {
        this.model = null
        this.error = 'Could not load this agent. ' + this.errText(err)
      } finally {
        this.loading = false
      }
    },

    // A field is shown as "inherited" when the pack's own file doesn't set it but
    // _base does (so the value on screen comes from base).
    isInherited (field) {
      return !(field in this.raw0) && (field in this.base)
    },

    addRole () {
      this.model.roles.push({ id: '', name: '', focus: '', audience: '', emphasis: [], key_questions: [] })
    },

    // Drop empty strings from a list; return undefined if nothing is left.
    cleanList (list) {
      const out = (list || []).map(s => (typeof s === 'string' ? s.trim() : s)).filter(s => !isEmpty(s))
      return out
    },

    // Build the value to persist for one field, cleaning empties.
    cleanField (field, val) {
      if (field === 'roles') {
        return (val || []).filter(r => (r.name || '').trim() || (r.id || '').trim()).map(r => {
          const o = { id: r.id, name: r.name, focus: r.focus }
          if ((r.audience || '').trim()) o.audience = r.audience
          const emph = this.cleanList(r.emphasis); if (emph.length) o.emphasis = emph
          const kq = this.cleanList(r.key_questions); if (kq.length) o.key_questions = kq
          return o
        })
      }
      if (field === 'translation_targets') {
        return (val || []).filter(t => (t.name || '').trim() || (t.id || '').trim()).map(t => {
          const o = { id: t.id, name: t.name }
          const emph = this.cleanList(t.emphasises); if (emph.length) o.emphasises = emph
          if ((t.blurb || '').trim()) o.blurb = t.blurb
          return o
        })
      }
      if (field === 'measure_families') {
        return (val || []).filter(m => (m.name || '').trim() || (m.id || '').trim())
          .map(m => ({ id: m.id, name: m.name, note: m.note || '' }))
      }
      if (['hard_rules', 'rag_hard_rules', 'routing_rules', 'corpus_hint'].includes(field)) {
        return this.cleanList(val)
      }
      return val
    },

    async save () {
      if (this.saving) return
      this.saving = true
      try {
        // Start from the pack's own file so unsurfaced fields (e.g. process_model)
        // and identity (id/parent) are preserved exactly.
        const out = clone(this.raw0) || {}
        out.id = this.selectedId
        EDITED_FIELDS.forEach(f => {
          const val = this.cleanField(f, this.model[f])
          const baseVal = this.base[f]
          if (ALWAYS_KEEP.has(f)) {
            if (isEmpty(val)) delete out[f]
            else out[f] = val
            return
          }
          if (deepEqual(val, baseVal)) {
            delete out[f] // same as base → keep inheriting, don't write it
          } else if (isEmpty(val) && !(f in this.raw0)) {
            delete out[f] // empty and wasn't set before → leave inherited/unset
          } else {
            out[f] = val // a real override
          }
        })
        await saveAdminContext(this.selectedId, out)
        this.raw0 = out // reflect the new saved state (inheritance badges update)
        this.snapshot = JSON.stringify(this.model) // now clean
        // Keep the rail's name/status badge in sync without a full reload.
        const railEntry = this.contexts.find(c => c.id === this.selectedId)
        if (railEntry) {
          railEntry.name = this.model.name
          railEntry.status = this.model.status
        }
        this.saveFlash = true
        setTimeout(() => { this.saveFlash = false }, 2500)
      } catch (err) {
        window.alert('Not saved. ' + this.errText(err))
      } finally {
        this.saving = false
      }
    },

    openAddAgent () {
      this.addAgentName = ''
      this.addAgentParent = 'sport'
      this.addAgentError = ''
      this.addAgentOpen = true
      this.$nextTick(() => {
        const el = this.$refs.addAgentNameInput
        if (el) el.focus()
      })
    },

    closeAddAgent () {
      if (this.addAgentBusy) return
      this.addAgentOpen = false
    },

    async submitAddAgent () {
      const name = this.addAgentName.trim()
      if (!name || this.addAgentBusy) return
      this.addAgentBusy = true
      this.addAgentError = ''
      try {
        const { data } = await createAdminContext(name, this.addAgentParent)
        this.addAgentOpen = false
        await this.loadContexts()
        // Switching agent is guarded by `dirty`; a fresh scaffold is never dirty.
        await this.loadPack(data.id)
        this.tab = 'behaviour'
      } catch (err) {
        this.addAgentError = this.errText(err)
      } finally {
        this.addAgentBusy = false
      }
    },

    async loadFiles () {
      try {
        const { data } = await getAdminFiles()
        this.files = data.files || []
        this.folders = data.folders || []
      } catch (err) {
        this.files = []
        this.folders = []
      }
    },

    toggleFolder (path) {
      this.$set(this.openPaths, path, !this.openPaths[path])
    },

    onUploadHere (folderPath) {
      // Remember which folder to upload into, then open the file picker.
      this.uploadTarget = folderPath || ''
      this.uploadMsg = ''
      this.uploadState = null
      if (this.uploadTimer) { clearTimeout(this.uploadTimer); this.uploadTimer = null }
      if (this.$refs.uploader) {
        this.$refs.uploader.value = '' // allow re-picking the same file
        this.$refs.uploader.click()
      }
    },

    async onFilePicked (e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      this.uploading = true
      this.uploadName = file.name
      this.uploadMsg = ''
      try {
        const { data } = await uploadKnowledgeFile(this.uploadTarget, this.selectedId, file, this.uploadAllowPii)
        this.uploading = false
        if (data.indexing && data.path) {
          // Saved; indexing continues in the background — poll for the real
          // outcome instead of assuming success (see pollUploadStatus).
          this.uploadState = 'indexing'
          this.uploadMsg = `Saved “${data.path}”. Indexing…`
          await this.loadFiles() // show the new file in the tree right away
          this.pollUploadStatus(data.path)
        } else if (data.report && data.path) {
          // Landed under an org's reports_dir (Cognitive/Personality report
          // PDFs) — the backend saves these straight to disk and never
          // indexes them, so there's no indexing job to poll for.
          this.uploadState = 'ok'
          this.uploadMsg = `Saved “${data.path}” as a per-candidate report — not indexed.`
          await this.loadFiles()
        } else {
          // Older/unexpected response shape — fall back to the old contract
          // rather than silently showing nothing.
          this.uploadState = data.indexed ? 'ok' : (data.saved ? 'warn' : 'failed')
          this.uploadMsg = data.detail || (data.indexed ? `Uploaded and indexed “${data.path}”.` : 'Upload failed.')
          await this.loadFiles()
        }
      } catch (err) {
        this.uploading = false
        this.uploadState = 'failed'
        this.uploadMsg = 'Upload failed. ' + this.errText(err)
      }
    },

    async pollUploadStatus (path) {
      try {
        const { data } = await getUploadStatus(path)
        if (data.state === 'indexing') {
          this.uploadTimer = setTimeout(() => this.pollUploadStatus(path), 4000)
          return
        }
        if (data.state === 'indexed') {
          this.uploadState = 'ok'
          this.uploadMsg = `Uploaded and indexed “${path}” (${data.chunks || 0} chunks).`
        } else if (data.state === 'failed') {
          // Saved to disk but NOT indexed — this is a PERMANENT rejection
          // (personal-data guardrail, unreadable/too-short content, etc.),
          // never a pending state that resolves on its own.
          this.uploadState = 'warn'
          this.uploadMsg = data.detail || `Saved “${path}”, but it was not indexed.`
        } else {
          // 'unknown' — server restarted mid-poll, or this path was never
          // tracked. Fall back to the file tree's ground-truth indexed flag.
          this.uploadState = null
          this.uploadMsg = `Upload finished for “${path}” — check the file list below for its indexed status.`
        }
        this.loadFiles()
      } catch (err) {
        this.uploadState = 'failed'
        this.uploadMsg = `Could not check indexing status for “${path}”. ` + this.errText(err)
      }
    },

    async doReingest () {
      this.reingestConfirm = false
      try {
        await startReingest()
        this.reingest = { running: true, log: ['Re-ingest started…'], ok: null, orgs: [], finished_at: null }
        this.pollReingest()
      } catch (err) {
        if (err.response && err.response.status === 409) {
          this.pollReingest()
        } else {
          window.alert('Could not start re-ingest. ' + this.errText(err))
        }
      }
    },

    async pollReingest () {
      try {
        const { data } = await getReingestStatus()
        const prevOrgs = this.reingest.orgs
        this.reingest = {
          running: !!data.running,
          log: data.log || [],
          ok: data.ok === undefined ? null : data.ok,
          orgs: data.orgs || [],
          finished_at: data.finished_at || null
        }
        // Refresh the file list as soon as ANY org finishes, not only once the
        // whole run does — capacio_general can finish in minute 2 of a
        // 20-minute run, and its live/not-live status shouldn't sit stale in
        // the tree and the org-wide panel behind hammarby/ifk still running.
        const orgJustFinished = this.reingest.orgs.some(o => {
          const prev = prevOrgs.find(p => p.id === o.id)
          return (o.status === 'ok' || o.status === 'failed') && (!prev || prev.status !== o.status)
        })
        if (orgJustFinished) this.loadFiles()
        if (data.running) {
          this.reingestTimer = setTimeout(this.pollReingest, 4000)
        } else if (data.ok === true) {
          this.indexStale = false
          this.loadFiles()
        }
      } catch (err) {
        this.reingest.running = false
      }
    },

    // localStorage, not the /admin/reingest response — dismissal is a purely
    // client-side preference, so a page reload or a re-login shouldn't bring
    // the banner back for a run the user already acknowledged.
    loadReingestDismissedAt () {
      try {
        return localStorage.getItem('capacio-admin-reingest-dismissed-at') || ''
      } catch (err) {
        return ''
      }
    },
    dismissReingest () {
      this.reingestDismissedAt = this.reingest.finished_at || ''
      try {
        localStorage.setItem('capacio-admin-reingest-dismissed-at', this.reingestDismissedAt)
      } catch (err) {
        // Private browsing / storage disabled — dismissal just won't survive a reload.
      }
    },

    buildTree (files, folders) {
      const root = { children: {} }
      // Seed empty-folder paths first — files (below) fill in / add to them, but
      // a folder with no files at all would otherwise never get a node.
      const emptyFolders = folders || []
      emptyFolders.forEach(path => {
        const parts = path.split('/')
        let node = root
        parts.forEach((part, idx) => {
          const p = parts.slice(0, idx + 1).join('/')
          if (!node.children[part]) node.children[part] = { name: part, path: p, type: 'folder', children: {} }
          node = node.children[part]
        })
      })
      files.forEach(f => {
        const parts = f.path.split('/')
        let node = root
        parts.forEach((part, idx) => {
          const isFile = idx === parts.length - 1
          const path = parts.slice(0, idx + 1).join('/')
          if (isFile) {
            node.children[part] = { name: part, path, type: 'file', source: f.source, size: f.size, indexed: !!f.indexed }
          } else {
            if (!node.children[part]) {
              node.children[part] = { name: part, path, type: 'folder', children: {} }
            }
            node = node.children[part]
          }
        })
      })
      const toArray = (obj) => Object.values(obj).map(n => {
        if (n.type === 'folder') n.children = toArray(n.children)
        return n
      }).sort((a, b) => {
        if (a.type !== b.type) return a.type === 'folder' ? -1 : 1
        return a.name.localeCompare(b.name)
      })
      return toArray(root.children)
    },

    // Recursively drop any folder node named `name`, at any depth.
    stripNamed (nodes, name) {
      return nodes
        .filter(n => n.name !== name)
        .map(n => n.type === 'folder' ? { ...n, children: this.stripNamed(n.children, name) } : n)
    },

    iconFor (c) {
      const map = { general: 'brain', football: 'football', handball: 'handball', business: 'chart' }
      return map[c.id] || map[c.parent] || 'compass'
    },

    async onRenameItem ({ node, newName }) {
      try {
        await renameKnowledgeItem(node.path, newName)
        this.indexStale = true
        await this.loadFiles()
      } catch (err) {
        window.alert('Rename failed. ' + this.errText(err))
      }
    },

    onMoveItem (node) {
      this.moveTarget = node
      this.moveDest = ''
      this.moveError = ''
    },

    async confirmMove () {
      if (!this.moveTarget) return
      const dest = this.moveDest === '(root)' ? '' : (this.moveDest || '')
      try {
        await moveKnowledgeItem(this.moveTarget.path, dest)
        this.moveTarget = null
        this.indexStale = true
        await this.loadFiles()
      } catch (err) {
        this.moveError = this.errText(err)
      }
    },

    cancelMove () {
      this.moveTarget = null
      this.moveError = ''
    },

    onCreateFolder (parentPath) {
      this.createFolderParent = parentPath || ''
      this.createFolderName = ''
      this.createFolderError = ''
      this.$nextTick(() => {
        const el = this.$refs.createFolderInput
        if (el) el.focus()
      })
    },

    async submitCreateFolder () {
      const name = this.createFolderName.trim()
      if (!name) return
      this.createFolderBusy = true
      this.createFolderError = ''
      try {
        await createKnowledgeFolder(this.createFolderParent, name)
        this.createFolderParent = null
        // A new empty folder isn't indexed, so it doesn't make the search index
        // stale — unlike rename/move, no indexStale flag here.
        await this.loadFiles()
      } catch (err) {
        this.createFolderError = this.errText(err)
      } finally {
        this.createFolderBusy = false
      }
    },

    cancelCreateFolder () {
      this.createFolderParent = null
      this.createFolderError = ''
    },

    async loadSkipDirs (context) {
      try {
        const { data } = await getSkipDirs(context)
        this.skipInfo = { corpusPrefix: data.corpus_prefix || '', skipDirs: data.skip_dirs || [] }
      } catch (err) {
        // No org backs this context yet (e.g. a brand-new template agent) — no
        // toggle-eligible folders, nothing to report as an error.
        this.skipInfo = { corpusPrefix: '', skipDirs: [] }
      }
    },

    async onToggleSkip (folderName) {
      const skip = !this.skipInfo.skipDirs.includes(folderName)
      try {
        const { data } = await setSkipDir(this.selectedId, folderName, skip)
        this.skipInfo = { ...this.skipInfo, skipDirs: data.skip_dirs || [] }
        this.indexStale = true
      } catch (err) {
        window.alert('Could not update the search-index setting. ' + this.errText(err))
      }
    },

    async onDeleteItem (node) {
      const kind = node.type === 'folder' ? 'folder' : 'file'
      const warning = node.type === 'folder'
        ? `Delete the folder "${node.name}" and everything in it? This cannot be undone.`
        : `Delete "${node.name}"? This cannot be undone.`
      if (!window.confirm(warning)) return
      try {
        await deleteKnowledgeItem(node.path)
        this.indexStale = true
        await this.loadFiles()
      } catch (err) {
        window.alert(`Could not delete this ${kind}. ` + this.errText(err))
      }
    },

    errText (err) {
      if (err.response) {
        const d = err.response.data
        return (d && d.detail) || `HTTP ${err.response.status}`
      }
      return err.message || 'Network error'
    }
  }
}
</script>

<style scoped>
.admin-dash {
  background: #f7f6f3;
  height: 100%;
}
.dash-header {
  border-bottom: 1px solid #e8e4dd;
  background: #fff;
}
.dash-sub {
  font-size: 1.3rem;
  color: #8a9a90;
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 1.2rem;
}
.back-link {
  font-size: 1.3rem;
  color: #8a6a3f;
  text-decoration: none;
}
.back-link:hover {
  text-decoration: underline;
}
.help-btn {
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 50%;
  border: 1px solid #8a6a3f;
  background: #fff;
  color: #8a6a3f;
  font-size: 1.3rem;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
}
.help-btn:hover {
  background: #f5efe4;
}

/* Left rail — styled after the analysis-environment cards on the context
   picker (chatbot.scss .ctx-card): white cards on a warm canvas, a round
   bronze icon mark, bronze border on the active/selected one. */
.agent-rail {
  width: 26rem;
  border-right: 1px solid #e8e4dd;
  background: #f7f6f3;
  padding: 1.2rem 0.8rem;
}
.rail-heading {
  font-size: 1.1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6f6d68;
  padding: 0.4rem 0.8rem 0.8rem;
}
.agent-item {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  width: 100%;
  text-align: left;
  background: #ffffff;
  border: 1px solid #e8e4dd;
  padding: 0.9rem 0.9rem;
  border-radius: 12px;
  margin-bottom: 0.6rem;
  cursor: pointer;
  transition: box-shadow 0.15s, transform 0.15s, border-color 0.15s;
}
.agent-item:hover {
  box-shadow: 0 6px 16px rgba(30, 26, 18, 0.08);
  transform: translateY(-1px);
}
.agent-item.active {
  border-color: #8a6a3f;
  background: #fdfbf8;
}
.agent-icon {
  width: 3.6rem;
  height: 3.6rem;
  flex-shrink: 0;
  border-radius: 9999px;
  background: #f5efe4;
  color: #8a6a3f;
  display: flex;
  align-items: center;
  justify-content: center;
}
.agent-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.agent-name {
  font-size: 1.4rem;
  font-weight: 500;
  color: #14161a;
}
.agent-parent {
  font-size: 1.1rem;
  color: #6f6d68;
}
/* Add org/sport */
.agent-add {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  width: 100%;
  text-align: left;
  border: 1px dashed #cabda3;
  background: none;
  color: #8a6a3f;
  padding: 0.9rem 0.8rem;
  border-radius: 12px;
  font-size: 1.3rem;
  cursor: pointer;
}
.agent-add:hover {
  background: #f5efe4;
  border-color: #8a6a3f;
}
.agent-add-icon {
  font-size: 1.6rem;
  line-height: 1;
  width: 2rem;
  text-align: center;
  flex-shrink: 0;
}
.add-agent-slug {
  font-size: 1.2rem;
  color: #8a9a90;
  margin: -1.6rem 0 2rem;
}
.add-agent-slug code {
  background: #eef2f5;
  border-radius: 3px;
  padding: 0.05rem 0.4rem;
}

/* Main */
.dash-main {
  /* No top padding here — .dash-tabs (sticky, top: 0) supplies its own top
     spacing via padding-top so that gap is covered by its background instead
     of sitting above it, where scrolled-past fields used to peek through. */
  padding: 0 2.4rem 1.6rem;
}
.dash-empty, .dash-error {
  padding: 2rem;
  color: #8a9a90;
  font-size: 1.4rem;
}
.dash-error {
  color: #c0392b;
}
.dash-tabs {
  display: flex;
  align-items: flex-end;
  gap: 0.4rem;
  border-bottom: 1px solid #e6ebe8;
  margin-bottom: 1.6rem;
  /* Stay pinned to the top of the scrolling column so Save is always reachable. */
  position: sticky;
  top: 0;
  z-index: 5;
  background: #fafbfa;
  padding-top: 1.6rem;
}
.dash-tabs-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-left: auto;
  padding-bottom: 0.5rem;
}
.dirty-note {
  font-size: 1.2rem;
  color: #eb6200;
}
.dash-tab {
  border: none;
  background: none;
  padding: 0.9rem 1.2rem;
  margin-bottom: -1px;
  font-size: 1.4rem;
  line-height: 1;
  height: auto;
  color: #8a9a90;
  cursor: pointer;
  border-bottom: 2px solid transparent;
}
.dash-tab.on {
  color: #46634f;
  border-bottom-color: #46634f;
  font-weight: 500;
}

/* Editor */
.editor {
  max-width: 76rem;
}
.field {
  display: block;
  margin-bottom: 2.4rem;
}
.field.narrow {
  max-width: 16rem;
}
.field-label {
  display: block;
  font-size: 1.4rem;
  font-weight: 500;
  color: #2b3a33;
}
.field-hint {
  display: block;
  font-size: 1.2rem;
  color: #8a9a90;
  margin-bottom: 0.6rem;
}
.inherited-badge {
  display: inline-block;
  font-size: 1rem;
  font-weight: 400;
  vertical-align: middle;
  margin-left: 0.6rem;
  padding: 0.1rem 0.5rem;
  border-radius: 4px;
  background: #eef2f5;
  border: 0.5px solid #b7c4cd;
  color: #57707f;
}
.field-textarea, .field-input {
  width: 100%;
  border: 1px solid #d5ddd8;
  border-radius: 6px;
  padding: 0.8rem 1rem;
  font-size: 1.35rem;
  font-family: inherit;
  color: #2b3a33;
  background: #fff;
  box-sizing: border-box;
}
.field-textarea {
  overflow: hidden;
  resize: none;
  min-height: 3.6rem;
  line-height: 1.5;
  display: block;
}
.field-input.full {
  width: 100%;
}
.field-textarea:focus, .field-input:focus {
  outline: none;
  border-color: #46634f;
}

/* Cards (roles / targets / families) */
.card {
  border: 1px solid #e6ebe8;
  border-radius: 8px;
  padding: 1.2rem;
  margin-bottom: 1rem;
  background: #fff;
}
.card-head {
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
}
.card-grid {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.8rem;
}
.card-remove {
  border: none;
  background: #f3f6f4;
  color: #8a9a90;
  border-radius: 4px;
  width: 3rem;
  height: 3.4rem;
  cursor: pointer;
  flex-shrink: 0;
}
.card-remove:hover {
  background: #fdecea;
  color: #c0392b;
}
.subfield {
  display: block;
  margin-top: 0.8rem;
}
.subfield-label {
  display: block;
  font-size: 1.15rem;
  color: #57707f;
  margin-bottom: 0.4rem;
}
.subfield-opt {
  color: #a4b1a9;
}
.row-add {
  border: 1px dashed #b7c4cd;
  background: none;
  color: #46634f;
  border-radius: 6px;
  padding: 0.6rem 1rem;
  font-size: 1.3rem;
  cursor: pointer;
}
.row-add:hover {
  background: #f1ffe6;
  border-color: #46634f;
}
.editor-actions {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  margin: 2.4rem 0 3rem;
}
.save-btn {
  background: #46634f;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.9rem 1.8rem;
  font-size: 1.4rem;
  cursor: pointer;
}
.save-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.save-note {
  font-size: 1.2rem;
  color: #08a133;
}

/* Knowledge */
.knowledge {
  max-width: 120rem;
}
.knowledge-body {
  display: flex;
  align-items: flex-start;
  gap: 1.6rem;
}
.knowledge-tree-col {
  flex: 1 1 auto;
  min-width: 0;
}
.knowledge-status-col {
  flex: 0 0 38rem;
  width: 38rem;
  position: sticky;
  top: 0;
  border: 1px solid #d5ddd8;
  border-radius: 6px;
  padding: 1.2rem;
  background: #fbfcfb;
}
.knowledge-status-title {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin: 0 0 0.6rem;
  font-size: 1.4rem;
  color: #2b3a33;
}
.knowledge-status-count {
  font-size: 1.1rem;
  font-weight: 600;
  color: #eb6200;
  background: #fff4ea;
  border: 0.5px solid #eb6200;
  border-radius: 999px;
  padding: 0.05rem 0.6rem;
}
.knowledge-status-hint {
  font-size: 1.15rem;
  color: #57707f;
  margin: 0 0 1rem;
}
.knowledge-status-empty {
  font-size: 1.2rem;
  color: #08a133;
}
.knowledge-status-groups {
  max-height: 60rem;
  overflow-y: auto;
}
.knowledge-status-group + .knowledge-status-group {
  margin-top: 1rem;
}
.knowledge-status-group-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 0.3rem;
  font-size: 1.15rem;
  font-weight: 600;
  color: #57707f;
  text-transform: capitalize;
}
.knowledge-status-group-count {
  font-size: 1rem;
  font-weight: 600;
  color: #57707f;
  background: #eef2f0;
  border-radius: 999px;
  padding: 0 0.5rem;
}
.knowledge-status-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.knowledge-status-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.6rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #eef2f0;
  font-size: 1.15rem;
}
.knowledge-status-item:last-child {
  border-bottom: none;
}
.knowledge-status-item .live-badge {
  margin-top: 0.1rem;
}
.knowledge-status-path {
  flex: 1;
  min-width: 0;
  overflow-wrap: break-word;
  word-break: break-word;
  white-space: normal;
  color: #2b3a33;
}

/* Shared with FileTree.vue's per-file live/not-live badge — kept in both
   scoped styles since Vue scoping doesn't cross component boundaries. */
.live-badge {
  font-size: 1.05rem;
  padding: 0.1rem 0.5rem;
  border-radius: 4px;
  border: 0.5px solid transparent;
  flex-shrink: 0;
}
.live-yes {
  background: #f1ffe6;
  border-color: #08a133;
  color: #08a133;
}
.live-no {
  background: #fff4ea;
  border-color: #eb6200;
  color: #eb6200;
}
.upload-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: #eef4f8;
  border: 1px solid #b7c4cd;
  border-radius: 6px;
  padding: 0.8rem 0.8rem 0.8rem 1.2rem;
  font-size: 1.3rem;
  color: #2b3a33;
  margin-bottom: 1.2rem;
}
.upload-status.ok {
  background: #f1ffe6;
  border-color: #08a133;
}
.upload-status.indexing {
  background: #eef4f8;
  border-color: #57707f;
  color: #57707f;
}
.upload-status.warn {
  background: #fff6e0;
  border-color: #d98c00;
  color: #7a5200;
}
.upload-status.failed {
  background: #fdecea;
  border-color: #c0392b;
  color: #c0392b;
}
.upload-pii-toggle {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.2rem;
  color: #57707f;
  margin-bottom: 0.8rem;
  cursor: pointer;
}
.notice-close {
  border: none;
  background: none;
  color: #57707f;
  font-size: 1.3rem;
  line-height: 1;
  cursor: pointer;
  padding: 0.3rem 0.5rem;
  border-radius: 4px;
  flex-shrink: 0;
}
.notice-close:hover {
  background: rgba(0, 0, 0, 0.06);
  color: #2b3a33;
}
.reingest-btn {
  border: 1px solid #eb6200;
  background: #fff4ea;
  color: #eb6200;
  border-radius: 6px;
  padding: 0.7rem 1.4rem;
  font-size: 1.3rem;
  cursor: pointer;
  flex-shrink: 0;
}
.reingest-btn:hover:not(:disabled) {
  background: #ffe9d6;
}
.reingest-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.reingest-status {
  border: 1px solid #d5ddd8;
  border-radius: 6px;
  padding: 1rem 1.2rem;
  margin-bottom: 1.2rem;
  background: #fff;
}
.reingest-status.done {
  border-color: #08a133;
  background: #f1ffe6;
}
.reingest-status.failed {
  border-color: #c0392b;
  background: #fdecea;
}
.reingest-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  font-size: 1.3rem;
  color: #2b3a33;
}
.reingest-log {
  margin: 0.8rem 0 0;
  font-size: 1.15rem;
  color: #57707f;
  white-space: pre-wrap;
  max-height: 16rem;
  overflow-y: auto;
}
.reingest-bar-track {
  margin-top: 0.8rem;
  height: 0.6rem;
  border-radius: 999px;
  background: #eef2f0;
  overflow: hidden;
}
.reingest-bar-fill {
  height: 100%;
  background: #eb6200;
  border-radius: 999px;
  transition: width 0.3s ease;
}
.reingest-bar-fill.failed {
  background: #c0392b;
}
.reingest-bar-label {
  margin-top: 0.3rem;
  font-size: 1.1rem;
  color: #57707f;
}
.reingest-orgs {
  list-style: none;
  margin: 0.8rem 0 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}
.reingest-org {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid #d5ddd8;
  border-radius: 999px;
  padding: 0.25rem 0.9rem;
  font-size: 1.15rem;
  color: #57707f;
  background: #fff;
}
.reingest-org.is-running {
  border-color: #eb6200;
  color: #eb6200;
}
.reingest-org.is-ok {
  border-color: #08a133;
  color: #08a133;
}
.reingest-org.is-failed {
  border-color: #c0392b;
  color: #c0392b;
  cursor: help;
}
.reingest-org-icon {
  font-size: 1.1rem;
}

/* Modal (namespaced to avoid the portal's global .modal styles) */
.dash-scrim {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 60;
  padding: 1rem;
}
.dash-modal {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  max-width: 46rem;
  width: 100%;
  padding: 2rem 2.2rem;
  visibility: visible;
  opacity: 1;
}
.dash-modal-title {
  font-size: 1.7rem;
  font-weight: 600;
  color: #2b3a33;
  margin: 0 0 0.8rem;
}
.dash-modal-body {
  font-size: 1.35rem;
  color: #57707f;
  margin: 0 0 1rem;
  line-height: 1.5;
}
.dash-modal-points {
  margin: 0 0 1.6rem;
  padding-left: 1.8rem;
  font-size: 1.3rem;
  color: #57707f;
  line-height: 1.7;
}
.dash-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}
.dash-modal-cancel {
  border: 1px solid #d5ddd8;
  background: #fff;
  color: #57707f;
  border-radius: 6px;
  padding: 0.8rem 1.6rem;
  font-size: 1.35rem;
  cursor: pointer;
}
.dash-modal-confirm {
  border: none;
  background: #eb6200;
  color: #fff;
  border-radius: 6px;
  padding: 0.8rem 1.6rem;
  font-size: 1.35rem;
  cursor: pointer;
}
.dash-modal-confirm:hover {
  background: #d25700;
}
.dash-modal-confirm:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* Stale-index reminder banner */
.stale-banner {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: #fffbea;
  border: 1px solid #f0c040;
  border-radius: 6px;
  padding: 0.8rem 1.2rem;
  font-size: 1.3rem;
  color: #7a5c00;
  margin-bottom: 1.2rem;
}
.stale-banner span {
  flex: 1;
}

/* Move-to picker */
.move-select {
  width: 100%;
  font-size: 1.35rem;
  border: 1px solid #d5ddd8;
  border-radius: 6px;
  padding: 0.6rem 0.8rem;
  margin-bottom: 1rem;
  color: #2b3a33;
  background: #fff;
}
.move-error {
  font-size: 1.2rem;
  color: #c0392b;
  margin: 0 0 0.8rem;
}

/* Help modal */
.help-modal {
  max-width: 54rem;
  max-height: 80vh;
  overflow-y: auto;
}
.help-subhead {
  font-size: 1.4rem;
  font-weight: 600;
  color: #2b3a33;
  margin: 1.6rem 0 0.4rem;
}
</style>
